import {useId, useRef} from "react";
import {motion} from "framer-motion";

interface BalanceChartProps {
    balances: number[];
    strokeWidth?: number;
    /** Fraction of chart height for the gradient fill (0-1, default 0.3) */
    gradientHeight?: number;
    /** Fraction of chart width each edge fades over (0-0.5, clamped; 0.5 = fades meet in middle) */
    edgeFade?: number;
    /** Line smoothing amount (0 = sharp corners, 1 = max curvature, default 0) */
    smoothing?: number;
    /** Fixed number of values — shorter arrays are left-padded with 0s to ensure consistent point count for morphing */
    valuesAmount?: number;
    /** Animation duration in seconds (default 0.4) */
    animationDuration?: number;
}

export const BalanceChart: React.FC<BalanceChartProps> = ({
    balances,
    strokeWidth = 0.5,
    gradientHeight = 1,
    edgeFade = 0,
    smoothing = 0,
    valuesAmount,
    animationDuration = 0.4,
}) => {
    const id = useId();
    const gradientId = `${id}-fill`;
    const prevPathRef = useRef<string | null>(null);
    const changeCountRef = useRef(0);

    // Pad balances to fixed length if valuesAmount is set
    const paddedBalances = valuesAmount && balances.length < valuesAmount
        ? [...new Array(valuesAmount - balances.length).fill(0), ...balances]
        : balances;

    const chartBalances = paddedBalances.length > 0 ? [0, ...paddedBalances] : [0];
    const finalBalance = paddedBalances.length > 0 ? paddedBalances[paddedBalances.length - 1] : 0;

    const getColors = (balances: number[]) => {
        if (balances.length >= 2) {
            const last = balances[balances.length - 1];
            const prev = balances[balances.length - 2];

            if (last > prev) {
                return {
                    line: 'rgb(34, 197, 94)',
                    gradientStart: 'rgba(34, 197, 94, 0.4)',
                    gradientEnd: 'rgba(34, 197, 94, 0)',
                };
            }
            if (last < prev) {
                return {
                    line: 'rgb(239, 68, 68)',
                    gradientStart: 'rgba(239, 68, 68, 0.4)',
                    gradientEnd: 'rgba(239, 68, 68, 0)',
                };
            }
        }

        return {
            line: 'rgb(209, 213, 219)',
            gradientStart: 'rgba(209, 213, 219, 0.4)',
            gradientEnd: 'rgba(209, 213, 219, 0)',
        };
    };

    const colors = getColors(chartBalances);

    const width = 100;
    const height = 100;

    const minValue = Math.min(...chartBalances);
    const maxValue = Math.max(...chartBalances);
    const range = maxValue - minValue || 1;

    const topPortion = 0.3;
    const effectiveMin = minValue - (range / topPortion) * (1 - topPortion);

    const mapY = (value: number) => {
        if (range === 1) {
            return height * 0.3;
        }
        const normalized = (value - effectiveMin) / (maxValue - effectiveMin);
        return height - normalized * height;
    };

    const points = chartBalances.length === 1
        ? [
            { x: 0, y: mapY(chartBalances[0]) },
            { x: width, y: mapY(chartBalances[0]) }
        ]
        : chartBalances.map((value, i) => ({
            x: (i / (chartBalances.length - 1)) * width,
            y: mapY(value),
        }));

    // Build line path with optional smoothing
    const buildPath = (pts: { x: number; y: number }[], s: number): string => {
        if (pts.length < 2) return '';
        if (s === 0 || pts.length === 2) {
            return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
        }

        const clamped = Math.min(Math.max(s, 0), 1);
        let d = `M ${pts[0].x},${pts[0].y}`;

        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[Math.max(i - 1, 0)];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[Math.min(i + 2, pts.length - 1)];

            const cp1x = p1.x + ((p2.x - p0.x) / 6) * clamped;
            const cp1y = p1.y + ((p2.y - p0.y) / 6) * clamped;
            const cp2x = p2.x - ((p3.x - p1.x) / 6) * clamped;
            const cp2y = p2.y - ((p3.y - p1.y) / 6) * clamped;

            d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
        }

        return d;
    };

    const linePath = buildPath(points, smoothing);

    // Calculate the max Y depth for the fill area
    const lineMinY = Math.min(...points.map(p => p.y));
    const lineMaxY = Math.max(...points.map(p => p.y));
    const availableBelow = height - lineMaxY;
    const fillBottomY = Math.min(height, lineMaxY + availableBelow * gradientHeight);

    const areaPath = `${linePath} L ${width},${fillBottomY} L 0,${fillBottomY} Z`;

    const clampedFade = Math.min(edgeFade, 0.5);
    const edgeFadePct = clampedFade * 100;
    const edgeFadeEndPct = 100 - clampedFade * 100;

    const wrapperStyle: React.CSSProperties = {
        display: 'block',
        width: '100%',
        height: '100%',
        ...(edgeFade > 0 ? {
            maskImage: `linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,1) ${edgeFadePct}%, rgba(0,0,0,1) ${edgeFadeEndPct}%, rgba(0,0,0,0) 100%)`,
            WebkitMaskImage: `linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,1) ${edgeFadePct}%, rgba(0,0,0,1) ${edgeFadeEndPct}%, rgba(0,0,0,0) 100%)`,
        } : {}),
    };

    // Track distinct path changes: skip animation for mount and first data load
    if (prevPathRef.current !== null && prevPathRef.current !== linePath) {
        changeCountRef.current += 1;
    }
    prevPathRef.current = linePath;

    const transition = changeCountRef.current >= 2
        ? { duration: animationDuration, ease: "easeInOut" as const }
        : { duration: 0 };

    // Add padding above lineMinY to prevent stroke clipping
    const topPadding = 2;
    const viewMinY = lineMinY - topPadding;

    return (
        <div style={wrapperStyle}>
            <svg
                width="100%"
                height="100%"
                viewBox={`0 ${viewMinY} ${width} ${fillBottomY - viewMinY}`}
                preserveAspectRatio="none"
                style={{ display: 'block' }}
            >
                <defs>
                    <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="0" y1={lineMinY} x2="0" y2={fillBottomY}>
                        <stop offset="0%" stopColor={colors.gradientStart} />
                        <stop offset="100%" stopColor={colors.gradientEnd} />
                    </linearGradient>
                </defs>

                <motion.path
                    initial={false}
                    animate={{ d: areaPath }}
                    transition={transition}
                    fill={`url(#${gradientId})`}
                />

                <motion.path
                    initial={false}
                    animate={{ d: linePath }}
                    transition={transition}
                    fill="none"
                    stroke={colors.line}
                    strokeWidth={strokeWidth}
                    vectorEffect="non-scaling-stroke"
                />
            </svg>
        </div>
    );
};